import { alpha, type Theme } from '@mui/material';

export type TradeScoreLevel = 'ELITE' | 'GOOD' | 'FAIR' | 'AVG.' | 'POOR';

export interface TradeScoreColors {
  border: string;
  background: string;
}

export interface TradeScoreLevelConfig {
  level: TradeScoreLevel;
  themePaletteKey: keyof Theme['palette'];
  minScore: number;
}

export const TRADE_SCORE_LEVELS: TradeScoreLevelConfig[] = [
  { level: 'ELITE', themePaletteKey: 'success', minScore: 80 },
  { level: 'GOOD', themePaletteKey: 'purple', minScore: 60 },
  { level: 'FAIR', themePaletteKey: 'blue', minScore: 40 },
  { level: 'AVG.', themePaletteKey: 'orange', minScore: 20 },
  { level: 'POOR', themePaletteKey: 'error', minScore: 0 },
];

export const TRADE_SCORE_PALETTE_KEY_MAP: Record<
  TradeScoreLevel,
  keyof Theme['palette']
> = {
  ELITE: 'success',
  GOOD: 'purple',
  FAIR: 'blue',
  'AVG.': 'orange',
  POOR: 'error',
};

export const getScoreLevel = (score: number): TradeScoreLevel => {
  // Find the highest level where score meets the minimum requirement
  // Levels are ordered from highest to lowest minScore
  for (const config of TRADE_SCORE_LEVELS) {
    if (score >= config.minScore) {
      return config.level;
    }
  }
  // Fallback to POOR if score is below all thresholds
  return 'POOR';
};

export const getScoreColor = (
  score: number,
  theme: Theme,
): TradeScoreColors => {
  const level = getScoreLevel(score);
  const paletteKey = TRADE_SCORE_PALETTE_KEY_MAP[level];
  const paletteColor = theme.palette[paletteKey] as { main: string };
  const borderColor = paletteColor.main;
  const backgroundColor = alpha(borderColor, 0.2);

  return {
    border: borderColor,
    background: backgroundColor,
  };
};

export const getScorePaletteKey = (score: number): keyof Theme['palette'] => {
  const level = getScoreLevel(score);
  return TRADE_SCORE_PALETTE_KEY_MAP[level];
};

export type ParameterScoreRange =
  | 'elite'
  | 'good'
  | 'fair'
  | 'average'
  | 'poor';

/**
 * Determines the score range for a parameter based on its numeric score
 * Ranges: Elite (80-100), Good (60-79), Fair (40-59), Avg. (20-39), Poor (0-19)
 * Returns null for invalid scores (null, undefined, NaN)
 */
export const getParameterScoreRange = (
  score: number | null | undefined,
): ParameterScoreRange | null => {
  if (score === null || score === undefined || isNaN(score)) {
    return null;
  }

  if (score >= 80) return 'elite';
  if (score >= 60) return 'good';
  if (score >= 40) return 'fair';
  if (score >= 20) return 'average';
  return 'poor';
};
